import {useSelector} from "react-redux";
import {useEffect, useRef, useState} from "react";
import {IStore} from "@/redux/defaultStore";
import {overwolfGameInfo} from "@/hooks/useOverwolfInterop";
import useGetOverwolfLauncherGameIds from "@/hooks/useGetOverwolfLauncherGameIds";

const NON_LAUNCHER_HOLDOVER_MS = 5 * 60 * 1000;
const GAME_STOPPED_DEBOUNCE_MS = 15 * 1000;

/*
 * Wraps `metaStore.overwolfGameInfo` with launcher-suppression and game-stopped debounce
 * so the UI doesn't flicker when GEP momentarily loses a real game's hook (anti-cheat
 * revocation by Ricochet, EAC, etc.) and reattaches to the platform launcher underneath.
 *
 * Suppression rules:
 *  - If incoming gameInfo has gameId in the launcher list AND a non-launcher game was
 *    the last seen running session within the last 5 minutes, ignore the update and
 *    keep the non-launcher state visible.
 *  - If incoming gameInfo says isGameRunning=false, hold the previous running state for
 *    15s. If a same-gameId running update lands during that window, cancel the debounce
 *    (transient drop). If the window expires, commit the stop transition.
 *
 * This is a render-layer filter only — the underlying Redux state still mutates as the
 * DRPC events fire. Consumers that should see the stable session use this hook;
 * useOverwolfInterop and other diagnostic surfaces keep reading the raw store.
 */
export default function useFilteredOverwolfGameInfo(): overwolfGameInfo | null {
    const rawGameInfo = useSelector((store: IStore) => store.metaStore.overwolfGameInfo);
    const {data: launcherData} = useGetOverwolfLauncherGameIds();
    const launcherGameIds = launcherData?.gameIds ?? [];

    const [filteredGameInfo, setFilteredGameInfo] = useState<overwolfGameInfo | null>(rawGameInfo);
    const lastNonLauncherRef = useRef<{ info: overwolfGameInfo; at: number } | null>(null);
    const stopDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const launcherSet = new Set(launcherGameIds);
        const incoming = rawGameInfo;
        const isLauncherIncoming = !!incoming?.gameId && launcherSet.has(incoming.gameId);

        if (incoming?.isGameRunning && !isLauncherIncoming) {
            /*
             * Real game running. Cancel any pending stop-debounce — a transient drop
             * was followed by a re-detect, so we never wanted to clear in the first place.
             */
            if (stopDebounceTimerRef.current) {
                clearTimeout(stopDebounceTimerRef.current);
                stopDebounceTimerRef.current = null;
            }
            lastNonLauncherRef.current = {info: incoming, at: Date.now()};
            setFilteredGameInfo(incoming);
            return;
        }

        if (incoming?.isGameRunning && isLauncherIncoming) {
            /*
             * Launcher claimed the foreground. If a non-launcher was running within
             * the holdover window, ignore this update and keep the non-launcher visible.
             */
            const recentNonLauncher = lastNonLauncherRef.current;
            if (recentNonLauncher && Date.now() - recentNonLauncher.at < NON_LAUNCHER_HOLDOVER_MS) {
                return;
            }
            // No recent non-launcher; let the launcher state through.
            setFilteredGameInfo(incoming);
            return;
        }

        // incoming is null or isGameRunning === false: debounce the stop transition.
        if (filteredGameInfo?.isGameRunning) {
            if (stopDebounceTimerRef.current) {
                clearTimeout(stopDebounceTimerRef.current);
            }
            stopDebounceTimerRef.current = setTimeout(() => {
                setFilteredGameInfo(incoming);
                stopDebounceTimerRef.current = null;
            }, GAME_STOPPED_DEBOUNCE_MS);
            return;
        }

        // Already stopped or no prior state; commit directly.
        setFilteredGameInfo(incoming);
    }, [rawGameInfo, launcherGameIds.join(",")]);

    useEffect(() => () => {
        if (stopDebounceTimerRef.current) {
            clearTimeout(stopDebounceTimerRef.current);
        }
    }, []);

    return filteredGameInfo;
}
